import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  InputBase,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Custom styled input
const CustomInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 16,
    position: 'relative',
    backgroundColor: theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
    border: '2px solid #95A0B3',
    fontSize: 16,
    height: 'auto',
    minHeight: 20,
    // width: 344,
    padding: '10px 0px 10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:focus': {
      borderColor: '#1976d2',
    },
  },
}));

// Mandatory Field Label Component
const MandatoryLabel = ({ children }) => (
  <Typography variant="subtitle2" gutterBottom>
    {children}
    <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
  </Typography>
);

export default function StopAddDetails({
  initialData = {},
  onSubmit,
  onPositionChange,
  updateMapPosition
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stopDetails, setStopDetails] = useState({
    stopId: '',
    stopName: '',
    stopLongitude: '',
    stopLatitude: '',
    landmark: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Track which field is being edited
  const [editingField, setEditingField] = useState(null);

  // Flag to prevent map updates during user editing
  const isUserEditing = useRef(false);

  // A flag to prevent map updates during initial data load
  const initialLoadComplete = useRef(false);

  // Flag to track if fields were manually cleared
  const fieldsManuallyCleared = useRef({
    stopLatitude: false,
    stopLongitude: false
  });

  useEffect(() => {
    if (initialData) {
      setStopDetails({
        stopId: initialData.stopId || '',
        stopName: initialData.name || '',
        stopLongitude: initialData.longitude || '',
        stopLatitude: initialData.latitude || '',
        landmark: initialData.landmark || '',
        description: initialData.description || ''
      });

      // Reset manually cleared flags
      fieldsManuallyCleared.current = {
        stopLatitude: false,
        stopLongitude: false
      };

      // Set the flag after initial load
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 500);
    }
  }, [initialData]);

  useEffect(() => {
    if (!onPositionChange || !initialLoadComplete.current || isUserEditing.current) return;

    // Only update if not manually cleared and not currently editing
    if (editingField !== 'stopLongitude' &&
      onPositionChange.longitude &&
      !fieldsManuallyCleared.current.stopLongitude) {
      setStopDetails(prev => ({
        ...prev,
        stopLongitude: onPositionChange.longitude
      }));
    }

    if (editingField !== 'stopLatitude' &&
      onPositionChange.latitude &&
      !fieldsManuallyCleared.current.stopLatitude) {
      setStopDetails(prev => ({
        ...prev,
        stopLatitude: onPositionChange.latitude
      }));
    }
  }, [onPositionChange, editingField]);

  const handleInputFocus = (e) => {
    const { name } = e.target;
    setEditingField(name);
    isUserEditing.current = true;

    // Reset the manually cleared flag when focusing again
    if (name === 'stopLatitude' || name === 'stopLongitude') {
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: false
      };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Mark field as manually cleared if value is empty
    if ((name === 'stopLatitude' || name === 'stopLongitude') && value === '') {
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: true
      };
    } else if (name === 'stopLatitude' || name === 'stopLongitude') {
      // Reset manually cleared flag when value is provided
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: false
      };
    }

    // Update the state for all fields
    setStopDetails(prev => ({
      ...prev,
      [name]: value
    }));

    // Only try to update map if both values exist and are valid numbers
    if ((name === 'stopLatitude' || name === 'stopLongitude')) {
      const lat = name === 'stopLatitude' ? value : stopDetails.stopLatitude;
      const lng = name === 'stopLongitude' ? value : stopDetails.stopLongitude;

      // Convert to strings for checking if they end with decimal
      const latStr = String(lat);
      const lngStr = String(lng);

      // Check if the input ends with a decimal point - still valid for input
      const isIncompleteDecimal = latStr.endsWith('.') || lngStr.endsWith('.');

      // Only update map if both values are valid numbers and not incomplete decimals
      if (lat && lng && !isIncompleteDecimal) {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);

        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          updateMapPosition(parsedLat, parsedLng);
        }
      }
    }
  };

  // Reset editing status after input blur
  const handleInputBlur = () => {
    // Use setTimeout to ensure the next field's focus is registered before clearing
    setTimeout(() => {
      setEditingField(null);
      isUserEditing.current = false;
    }, 200);
  };

  const validateForm = () => {
    const newErrors = {};
    const mandatoryFields = ['stopId', 'stopName', 'stopLongitude', 'stopLatitude'];

    mandatoryFields.forEach(field => {
      if (!stopDetails[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        stopId: stopDetails.stopId,
        name: stopDetails.stopName,
        longitude: stopDetails.stopLongitude,
        latitude: stopDetails.stopLatitude,
        landmark: stopDetails.landmark,
        description: stopDetails.description
      });
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/stop");
  };

  return (
    <Box
      sx={{
        height: 'auto',
        margin: "40px",
        borderRadius: 2,
        border: '1px solid #A5DCF3',
        boxShadow: '-2px 2px 5px 0px rgba(0,0,0,0.25)',
        p: 2
      }}
    >
      <Box
        sx={{
          height: 52,
          borderRadius: 1,
          backgroundColor: '#647490',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          px: 3,
          mb: 2,
          boxShadow: '-2px 2px 5px 0px rgba(0,0,0,0.25)'
        }}
      >
        <Typography variant="h6">{t("Stop Details")}</Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {/* Stop ID field */}
        <Grid item xs={12} sm={6} md={4}>
          <MandatoryLabel>{t("Stop ID")}</MandatoryLabel>
          <TextField
            fullWidth
            name="stopId"
            value={stopDetails.stopId}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Stop ID")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.stopId}
            helperText={errors.stopId}
            required={true}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        {/* Stop Name field */}
        <Grid item xs={12} sm={6} md={4}>
          <MandatoryLabel>{t("Stop Name")}</MandatoryLabel>
          <TextField
            fullWidth
            name="stopName"
            value={stopDetails.stopName}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Stop Name")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.stopName}
            helperText={errors.stopName}
            required={true}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        {/* Stop Longitude field */}
        <Grid item xs={12} sm={6} md={4}>
          <MandatoryLabel>{t("Stop Longitude")}</MandatoryLabel>
          <TextField
            fullWidth
            name="stopLongitude"
            value={stopDetails.stopLongitude}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Stop Longitude")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.stopLongitude}
            helperText={errors.stopLongitude}
            required={true}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        {/* Stop Latitude field */}
        <Grid item xs={12} sm={6} md={4}>
          <MandatoryLabel>{t("Stop Latitude")}</MandatoryLabel>
          <TextField
            fullWidth
            name="stopLatitude"
            value={stopDetails.stopLatitude}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Stop Latitude")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.stopLatitude}
            helperText={errors.stopLatitude}
            required={true}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        {/* Landmark field */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" gutterBottom>{t("Landmark")}</Typography>
          <TextField
            fullWidth
            name="landmark"
            value={stopDetails.landmark}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Landmark")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.landmark}
            helperText={errors.landmark}
            required={false}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        {/* Description field */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" gutterBottom>{t("Description")}</Typography>
          <TextField
            fullWidth
            name="description"
            value={stopDetails.description}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            variant="standard"
            placeholder={t("Type Description")}
            InputProps={{
              disableUnderline: true,
              inputComponent: CustomInput,
            }}
            error={!!errors.description}
            helperText={errors.description}
            required={false}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 1,
                position: 'absolute',
                bottom: '-1.5em'
              }
            }}
            sx={{
              position: 'relative',
              paddingBottom: '1.5em'
            }}
          />
        </Grid>

        <Grid item xs={12} container justifyContent="center" spacing={2} >
          <Grid item>
            <Button
              variant="contained"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                width: "122px",
                textTransform: "none",
                height: "55px",
                borderRadius: '8px',
                borderWidth: 1,
                paddingTop: '16px',
                paddingRight: '24px',
                paddingBottom: '16px',
                paddingLeft: '24px',
                background: '#F2F2F3',
                border: '1px solid #414958',
                fontSize:"20px",
                color:"#1F242E",
                '&:hover': {
                  background: '#E0E0E1',
                }
              }}
            >
              {t("Cancel")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                width: "125px",
                fontSize:"20px",
                color:"#1F242E",
                textTransform: "none",
                height: "55px",
                borderRadius: '8px',
                borderWidth: 1,
                paddingTop: '16px',
                paddingRight: '24px',
                paddingBottom: '16px',
                paddingLeft: '24px',
                background: '#F9E719',
                border: '1px solid #414958',
                ":hover":{
                  background: '#F9E718',
                }
              }}
            >
              {t("Save")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}